<script setup lang="ts">
defineOptions({
  inheritAttrs: false,
});

import { watch, ref, useId, useSlots, computed } from 'vue';

import { OverlayDialog, SCALE_FADE_TRANSITION } from '@/shared/ui/overlay';
import { AppSpinner } from '@/shared/ui/spinner';

// Определяем размеры модального окна
type ModalSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'full';

// Определяем позиции модального окна
type ModalPosition = 'center' | 'top' | 'bottom';

interface Props {
  show?: boolean;
  // По умолчанию клик по затемнению не закрывает модалку — данные формы не теряются случайно.
  closeOnBackdrop?: boolean;
  loading?: boolean;
  size?: ModalSize;
  position?: ModalPosition;
  persistent?: boolean;
  fullScreen?: boolean;
  closeOnEscape?: boolean;
  // Спрашивает перед закрытием крестиком, Escape или фоном; `false` оставляет окно открытым.
  beforeClose?: () => boolean | Promise<boolean>;
}

const props = withDefaults(defineProps<Props>(), {
  show: false,
  closeOnBackdrop: false,
  loading: false,
  size: 'md',
  position: 'center',
  persistent: false,
  fullScreen: false,
  closeOnEscape: true,
  beforeClose: undefined,
});

interface Emits {
  'update:show': [value: boolean];
  close: [];
}

const emit = defineEmits<Emits>();

const isOpen = ref(props.show);
const titleId = useId();

// Размеры модального окна
const sizeClasses = computed(() => {
  const sizes = {
    xs: 'max-w-xs',
    sm: 'max-w-sm',
    md: 'max-w-md lg:max-w-lg',
    lg: 'max-w-lg lg:max-w-2xl',
    xl: 'max-w-xl lg:max-w-4xl',
    full: 'max-w-full',
  };

  return sizes[props.size];
});

// Позиционирование модального окна
const positionClasses = computed(() => {
  const positions = {
    center: 'items-center justify-center',
    top: 'items-start justify-center pt-16',
    bottom: 'items-end justify-center pb-16',
  };

  return positions[props.position];
});

const themeClasses = {
  // Скрим одинаковый в обеих темах.
  backdrop: 'bg-(--scrim)',
  modal: 'bg-(--surface-card) text-(--text-primary)',
  closeButton: 'text-(--text-secondary) hover:text-(--text-primary)',
  title: 'text-(--text-primary)',
  border: 'border-(--border)',
};

// Отслеживаем изменения prop show
watch(
  () => props.show,
  (newValue) => {
    isOpen.value = newValue;
  },
);

watch(isOpen, (newValue) => {
  emit('update:show', newValue);

  if (!newValue) {
    emit('close');
  }
});

// Пока ждём ответа `beforeClose`, повторный Escape не спрашивает снова.
let closing = false;
const close = async (): Promise<void> => {
  if (props.persistent || closing) return;

  if (!props.beforeClose) {
    isOpen.value = false;
    return;
  }

  closing = true;
  try {
    if (await props.beforeClose()) isOpen.value = false;
  } finally {
    closing = false;
  }
};

// Фон — обёртка вокруг панели. Выделение текста, отпущенное за краем панели, тоже даёт click по обёртке,
// поэтому закрывает только нажатие на самом фоне.
let pressTarget: EventTarget | null = null;
const handleBackdropPress = (event: PointerEvent): void => {
  pressTarget = event.target;
};
const handleBackdropClick = (event: MouseEvent): void => {
  if (props.closeOnBackdrop && event.target === pressTarget) void close();
};

const slots = useSlots();
</script>

<template>
  <OverlayDialog
    :open="isOpen"
    :close-on-escape="closeOnEscape && !persistent"
    :backdrop-class="themeClasses.backdrop"
    :transition="SCALE_FADE_TRANSITION"
    :aria-labelledby="slots.title ? titleId : undefined"
    @request-close="close"
  >
    <div
      :class="['fixed inset-0 overflow-y-auto', fullScreen ? '' : 'p-4 md:p-6']"
      @pointerdown.capture="handleBackdropPress"
      @click.self="handleBackdropClick"
    >
      <div
        :class="['flex', positionClasses, fullScreen ? 'h-full' : 'min-h-full']"
        @click.self="handleBackdropClick"
      >
        <div
          :class="[
            'relative w-full rounded-lg shadow-xl',
            'transition-[transform,opacity] duration-200',
            fullScreen ? 'h-full max-h-full max-w-full rounded-none' : sizeClasses,
            themeClasses.modal,
            $attrs.class,
          ]"
        >
          <!-- Close Button -->
          <button
            v-if="!persistent"
            type="button"
            :class="[
              'absolute top-2 right-2 z-50 flex h-11 w-11 items-center justify-center rounded-full',
              'transition-[transform,color] duration-200 hover:rotate-90 active:scale-[0.96]',
              'focus-visible:ring-2 focus-visible:ring-(--accent) focus-visible:outline-none',
              themeClasses.closeButton,
            ]"
            aria-label="Закрыть модальное окно"
            @click="close"
          >
            <svg
              class="h-6 w-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>

          <!-- Loading State -->
          <div
            v-if="loading"
            class="flex h-24 items-center justify-center"
          >
            <AppSpinner class="h-10 w-10" />
          </div>

          <!-- Content -->
          <div
            v-else
            :class="['flex flex-col', fullScreen ? 'h-full p-4 md:p-6' : 'max-h-full p-4 md:p-6']"
          >
            <!-- Title -->
            <h3
              v-if="slots.title"
              :id="titleId"
              :class="['mb-8 shrink-0 text-2xl', 'select-none', themeClasses.title]"
            >
              <slot name="title" />
            </h3>

            <!-- Header -->
            <div
              v-if="slots.header"
              class="mb-4 shrink-0"
            >
              <slot name="header" />
            </div>

            <!-- Main Content -->
            <div class="min-h-0 flex-1">
              <slot />
            </div>

            <!-- Actions -->
            <div
              v-if="slots.actions"
              class="mt-6 shrink-0 pt-4"
              :class="[themeClasses.border]"
            >
              <slot name="actions" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </OverlayDialog>
</template>
